import Phaser from 'phaser';
import dimensions from '../dimensions';
import utils from '../utils';
import sounds from '../sounds';
import Button from '../objects/ui/Button';
import RadioButton from '../objects/ui/RadioButton';
import RadioButtonGroup from '../objects/ui/RadioButtonGroup';

export default class MenuState extends Phaser.State {
    init() {
        if (this.game.sound.usingWebAudio && this.game.sound.context.state === 'suspended') {
            this.game.input.onTap.addOnce(function () {
                this.game.sound.context.resume();
            }.bind(this));
        }
    }

    create() {
        this.setGameScale();
        this.createSounds();

        this.stage.backgroundColor = '#000000';
        this.game.gameState = this;

        this.supergroup = this.game.add.group();
        this.gameGroup = this.game.add.group();
        this.supergroup.add(this.gameGroup);
        this.game.time.advancedTiming = true;

        this.bg = this.game.add.sprite(dimensions.gameWidth / 2, dimensions.gameHeight / 2, "main_menu");
        this.bg.anchor.set(0.5);
        this.gameGroup.add(this.bg);

        this.bg.originalWidth = this.bg.width;
        this.bg.originalHeight = this.bg.height;

        this.createEnvironment();

        this.game.currentLevel = 0;

        this.setPositions();
        this.scale.setResizeCallback(this.gameResized, this);
        this.game.onBlur.add(this.onPause, this);
        this.game.onFocus.add(this.onResume, this);

        this.gameResized();
        this.loaded = false;

        sounds.menuMusic.play();
        sounds.missionMusic.stop();
        sounds.simMusic.stop();
    }

    onPause() {
        this.game.sound.pauseAll();
    }

    onResume() {
        this.game.sound.resumeAll();
    }

    update() {}

    createEnvironment() {
        this.logo = this.game.add.sprite(dimensions.gameWidth / 2, 75, "sheet", "title");
        this.logo.anchor.set(0.5);
        this.gameGroup.add(this.logo);

        this.createMissionWindow();
        this.createLevelWindow();
        this.createModeWindow();
        this.createHelpWindow();

        this.missionSelection.visible = true;
        this.levelSelection.visible = false;
        this.modeSelection.visible = false;
        this.helpWindow.visible = false;

        this.instructions = new Button(this.game, this.gameGroup, "radio-button", "?", 35, this.showHelpWindow.bind(this));
        this.instructions.x = 100;
        this.instructions.y = dimensions.gameHeight - 50;
    }

    createMissionWindow() {
        this.missionSelection = this.game.add.group();
        this.missionSelection.x = dimensions.gameWidth / 2;
        this.missionSelection.y = dimensions.gameHeight / 2;
        this.gameGroup.add(this.missionSelection);

        let bg = this.game.add.sprite(0, 25, "sheet", "ui-box");
        bg.anchor.set(0.5);
        this.missionSelection.add(bg);

        let text = this.game.add.bitmapText(0, -150, "font-title", "SELECT MISSION", 70);
        text.anchor.set(0.5);
        this.missionSelection.add(text);

        let names = ["The Home Row Keys", "The Top Row Keys", "The Bottom Row Keys", "The Shift Keys", "Enter & Backspace", "Tab & Caps Lock", "Fourth-Row Keys"];

        for (let i = 0; i < 7; i++) {
            let button = new Button(this.game, this.missionSelection, "mission-button", i + 1, 70, function (i) {
                sounds.click.play();
                this.game.currentMission = i + 1;
                this.missionSelection.visible = false;
                this.showLevelSelection();
            }.bind(this, i));
            button.x = (i - 1.5) * 170 - Math.floor(i / 4) * 595;
            button.y = -20 + 170 * Math.floor(i / 4);

            let text = this.game.add.bitmapText(0, 70, "font-text", names[i], 10);
            text.anchor.set(0.5);
            button.addChild(text);
        }

        let beginner = new RadioButton(this.game, this.game.add.group(), "beg", "Beginner", 50);
        beginner.x = -170;
        beginner.y = 300;
        beginner.scale.set(0.5);
        this.missionSelection.add(beginner);

        let advanced = new RadioButton(this.game, this.game.add.group(), "adv", "Advanced", 50);
        advanced.x = 30;
        advanced.y = 300;
        advanced.scale.set(0.5);
        this.missionSelection.add(advanced);

        this.difficulty = new RadioButtonGroup([beginner, advanced]);
    }

    createLevelWindow() {
        this.levelSelection = this.game.add.group();
        this.levelSelection.x = dimensions.gameWidth / 2;
        this.levelSelection.y = dimensions.gameHeight / 2;
        this.gameGroup.add(this.levelSelection);

        let bg = this.game.add.sprite(0, 25, "sheet", "ui-box");
        bg.anchor.set(0.5);
        this.levelSelection.add(bg);

        let mission = this.game.add.bitmapText(0, -170, "font-title", "MISSION 1", 35);
        mission.anchor.set(0.5);
        this.levelSelection.add(mission);

        this.levelSelection.mission = mission;

        let text = this.game.add.bitmapText(0, -110, "font-title", "SELECT LEVEL", 70);
        text.anchor.set(0.5);
        this.levelSelection.add(text);

        this.levelSelection.buttons = [];

        for (let i = 0; i < 3; i++) {
            let button = new Button(this.game, this.levelSelection, "mission-button", i + 1, 70, function (i) {
                sounds.click.play();
                this.game.currentLevel = i;
                this.levelSelection.visible = false;
                this.modeSelection.visible = true;

                this.modeSelection.mission.text = "Mission " + this.game.currentMission + " Level " + (this.game.currentLevel + 1);
            }.bind(this, i));
            button.x = (i - 1) * 170;
            button.y = 30;

            let text = this.game.add.bitmapText(0, 65, "font-text", "Level", 12);
            text.anchor.set(0.5, 0);
            text.align = "center";
            button.addChild(text);

            button.text = text;

            this.levelSelection.buttons.push(button);
        }

        let backButton = new Button(this.game, this.levelSelection, "mission-button", "<-", 70, function () {
            this.missionSelection.visible = true;
            this.levelSelection.visible = false;
            this.modeSelection.visible = false;
        }.bind(this));
        backButton.x = -320;
        backButton.y = 210;
        backButton.scale.set(0.5);
    }

    showLevelSelection() {
        // Dont show level selection window in case of only 1 level
        if (GameConfig.letters["Mission" + this.game.currentMission].length === 1) {
            this.game.currentLevel = 0;
            this.levelSelection.visible = false;
            this.modeSelection.visible = true;
            this.modeSelection.mission.text = "Mission " + this.game.currentMission + " Level " + (this.game.currentLevel + 1);

            return;
        }

        this.levelSelection.mission.text = "Mission " + this.game.currentMission;

        this.levelSelection.visible = true;

        for (let i = 0; i < 3; i++) {
            this.levelSelection.buttons[i].visible = false;
        }

        let spacing = GameConfig.letters["Mission" + this.game.currentMission].length - 1;
        spacing /= 2;

        let distance = 170 * 3 / GameConfig.letters["Mission" + this.game.currentMission].length;

        let names = [["The Stargate\na,s,d,f keys", "Ambush!\nj,k,l,; keys", "Atorri 11\ng,h keys"], ["Cannon Plans\nq,w,e,r keys", "Centari Warp\nu,i,o,p keys", "Must Cloak\nt,y keys"], ["Roden Moon\nz,x,c,v keys", "Install Cloak\nm,comma,period\nslash keys", "Find Generator\nb,n keys"]];

        for (let i = 0; i < GameConfig.letters["Mission" + this.game.currentMission].length; i++) {
            this.levelSelection.buttons[i].visible = true;
            this.levelSelection.buttons[i].x = (i - spacing) * distance;

            if (names[this.game.currentMission - 1]) {
                this.levelSelection.buttons[i].text.visible = true;
                this.levelSelection.buttons[i].text.text = names[this.game.currentMission - 1][i];
            } else {
                this.levelSelection.buttons[i].text.visible = false;
            }
        }
    }

    createModeWindow() {
        this.modeSelection = this.game.add.group();
        this.modeSelection.x = dimensions.gameWidth / 2;
        this.modeSelection.y = dimensions.gameHeight / 2;
        this.gameGroup.add(this.modeSelection);

        let bg = this.game.add.sprite(0, 25, "sheet", "ui-box");
        bg.anchor.set(0.5);
        this.modeSelection.add(bg);

        let mission = this.game.add.bitmapText(0, -180, "font-title", "MISSION 1 LEVEL 1", 35);
        mission.anchor.set(0.5);
        this.modeSelection.add(mission);

        this.modeSelection.mission = mission;

        let title = this.game.add.bitmapText(0, -130, "font-title", "SELECT MODE", 70);
        title.anchor.set(0.5);
        this.modeSelection.add(title);

        let modes = ["Mission\nSimulator", "Perform\nMission"];

        for (let i = 0; i < 2; i++) {
            let button = new Button(this.game, this.game.add.group(), "mode-button", modes[i], 50, function (i) {
                sounds.click.play();
                this.game.currentDifficulty = this.difficulty.current;
                if (i === 0) this.state.start('Practice');else this.state.start('Mission');
            }.bind(this, i));

            button.x = 0;
            button.y = -20 + i * 150;

            this.modeSelection.add(button);
        }

        let backButton = new Button(this.game, this.modeSelection, "mission-button", "<-", 70, function () {
            this.missionSelection.visible = true;
            this.levelSelection.visible = false;
            this.modeSelection.visible = false;
        }.bind(this));
        backButton.x = -320;
        backButton.y = 210;
        backButton.scale.set(0.5);
    }

    createHelpWindow() {
        this.helpWindow = this.game.add.group();
        this.helpWindow.x = dimensions.gameWidth / 2;
        this.helpWindow.y = dimensions.gameHeight / 2;
        this.gameGroup.add(this.helpWindow);

        let bg = this.game.add.sprite(0, 0, "sheet", "ui-box");
        bg.anchor.set(0.5);
        bg.scale.set(1.3);
        this.helpWindow.add(bg);

        let text = this.game.add.bitmapText(0, -260, "font-title", "INSTRUCTIONS", 70);
        text.anchor.set(0.5);
        this.helpWindow.add(text);

        let instructions = this.game.add.bitmapText(0, 40, "font-text", GameConfig.instructions, 17);
        instructions.anchor.set(0.5);
        instructions.align = "left";
        instructions.maxWidth = 800;
        this.helpWindow.add(instructions);

        let gameTitle = this.game.add.bitmapText(230, -207, "font-title", "Mission to Save Alpha Prime", 20);
        gameTitle.anchor.set(0.5);
        this.helpWindow.add(gameTitle);

        let backButton = new Button(this.game, this.helpWindow, "mission-button", "<-", 70, function () {
            this.helpWindow.visible = false;
            this.lastWindow.visible = true;
            this.logo.visible = true;
            this.instructions.visible = true;
        }.bind(this));
        backButton.x = -dimensions.gameWidth / 2 + 50;
        backButton.y = dimensions.gameHeight / 2 - 50;
        backButton.scale.set(0.5);
    }

    showHelpWindow() {
        if (this.missionSelection.visible) this.lastWindow = this.missionSelection;
        if (this.levelSelection.visible) this.lastWindow = this.levelSelection;
        if (this.modeSelection.visible) this.lastWindow = this.modeSelection;

        this.logo.visible = false;
        this.instructions.visible = false;
        this.lastWindow.visible = false;
        this.helpWindow.visible = true;
    }

    createSounds() {
        sounds.menuMusic = this.game.add.sound("menuMusic");
        sounds.comms = this.game.add.sound("comms");
        sounds.missionMusic = this.game.add.sound("missionMusic");
        sounds.simMusic = this.game.add.sound("simMusic");
        sounds.countdown = this.game.add.sound("countdown");
        sounds.explode = this.game.add.sound("explode");
        sounds.click = this.game.add.sound("button_click");

        sounds.click.volume = 0.5;
        sounds.menuMusic.volume = 0.5;
        sounds.missionMusic.volume = 0.5;
        sounds.simMusic.volume = 0.5;

        sounds.menuMusic.loop = true;
        sounds.comms.loop = true;
        sounds.missionMusic.loop = true;
        sounds.simMusic.loop = true;
    }

    gameResized() {
        let ratio = 1;

        if (window.screen.systemXDPI !== undefined && window.screen.logicalXDPI !== undefined && window.screen.systemXDPI > window.screen.logicalXDPI) ratio = window.screen.systemXDPI / window.screen.logicalXDPI;else if (window.devicePixelRatio !== undefined) ratio = window.devicePixelRatio;

        dimensions.fullWidth = window.innerWidth * ratio;
        dimensions.fullHeight = window.innerHeight * ratio;

        if (this.game.width === dimensions.fullWidth && this.game.height === dimensions.fullHeight) return;

        this.game.scale.setGameSize(dimensions.fullWidth, dimensions.fullHeight);

        this.setGameScale();
        this.setPositions();
    }

    setGameScale() {
        let scaleX = dimensions.fullWidth / dimensions.gameWidth;
        let scaleY = dimensions.fullHeight / dimensions.gameHeight;

        this.gameScale = scaleX < scaleY ? scaleX : scaleY;

        dimensions.actualWidth = this.game.width / this.gameScale;
        dimensions.actualHeight = this.game.height / this.gameScale;
        dimensions.viewWidth = dimensions.actualWidth;
        dimensions.viewHeight = 764;
    }

    setPositions() {
        this.supergroup.scale.set(this.gameScale);
        this.gameGroup.x = (this.game.width / this.gameScale - dimensions.gameWidth) / 2;

        let scaleX = dimensions.actualWidth / this.bg.originalWidth;
        let scaleY = dimensions.actualHeight / this.bg.originalHeight;
        let scale = Math.max(scaleX, scaleY);

        this.bg.scale.set(scale);
        this.bg.y = dimensions.actualHeight / 2;
    }

    offsetMouse() {
        return { x: this.game.input.x * dimensions.actualWidth / this.game.width + (dimensions.gameWidth - dimensions.actualWidth) / 2,
            y: this.game.input.y * dimensions.actualHeight / this.game.height };
    }

    offsetWorld(point) {
        return { x: point.x * dimensions.actualWidth / this.game.width, y: point.y * dimensions.actualHeight / this.game.height };
    }
}


//////////////////
// WEBPACK FOOTER
// ./src/states/Menu.js
// module id = 147
// module chunks = 0